//@ts-nocheck
import { Badge, Button, DialogContainer, Modal, ModalFooter, ModalTitle, ModalTitleContainer, ProgressMetadata } from "@factorearth/component-library";
import { DropResult } from "@hello-pangea/dnd";
import { generateClient, GraphQLResult } from "aws-amplify/api";
import Svg from "components/Svg/Svg";
import { db } from "db/db";
import { FieldInstance, FormGroup as FormGroupDef, FormInstance as FormInstanceDef, FormTemplate, Project, UserForResolver } from "db/definitions";
import { liveQuery } from "dexie";
import { Location } from "hooks/useDrawer";
import React, { useEffect, useRef, useState } from "react";
import getFullFormTemplate from "utils/getFullFormTemplate";
import { recordSentry } from "utils/recordSentry";
import { selectPrimaryFieldInstances } from "utils/selectPrimaryFieldInstances";
import { failedWriteCount, isConnectionDegraded, subscribeToConnectionStatus } from "utils/connectionStatus";
import { v4 as uuid } from "uuid";

/** Form instance ids we have already reported as carrying duplicate field instances, so Sentry gets one event per form per session. */
const reportedDuplicateForms = new Set<string>();
import FieldAction from "components/FieldAction/FieldAction";
import { deleteFormInstance as deleteFormI } from "db/queries/formInstanceQueries";
// @ts-ignore
import { getFormInstance } from "graphql/queries";
import { newOnUpdatedFieldInstance, newOnUpdatedFormGroup } from "graphql/subscriptions";
import useClickOutside from "hooks/useClickOutside";
import { useTheme } from "hooks/useSingleton";
import useStateRef from "hooks/useStateRef";
import { _updateFormGroup } from "utils/_updateFormGroup";
import deleteFormInstanceAmplify from "utils/deleteFormInstance";
import getOptions from "utils/getOptions";
import reorder from "utils/reorder";
import { UpdateContext } from "utils/UpdateContext";
import updateFormGroupAmplify from "utils/updateFormGroup";
import styles from "./FormGroup.module.scss";
import FormGroupOptions from "./FormGroupOptions/FormGroupOptions";
import FormInstance from "./FormInstance/FormInstance";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import { FiX } from "react-icons/fi";
import { duplicateFormInstance } from "utils/duplicateFormInstance";

const dropdownData = [{ id: 0, title: "Form Manager" }];

interface Props {
	createNewInstance: (
		template: FormTemplate,
		formGroup: FormGroupDef,
		linkedAltIds: Record<string, string>,
		linkedData: Record<string, FieldInstance>,
	) => Promise<FormInstanceDef | void>;
	formGroupId: string;
	getSingleFormGroup: (id: string) => Promise<FormGroupDef | void>;
	online: boolean;
	project: Project | null;
	setProgress: (newProgress: Partial<ProgressMetadata>) => void;
	setupDrawer: (location: Location | "") => void;
	userDoc: UserForResolver;
}

function FormGroup(props: Props) {
	const { createNewInstance, formGroupId, getSingleFormGroup, online, project, setProgress, setupDrawer, userDoc } = props;
	const color = useTheme();

	const [formGroupHtml, setFormGroupHtml] = useState<React.JSX.Element[]>([]);
	// The linked alt ids and linked data are for the functionality to track linked field instances
	// alt ids specifically for the creation of new instances that require a linked alt id
	// This is probably not the best way to go about it..
	const [linkedAltIds, setLinkedAltIds] = useState<Record<string, string>>({});
	const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
	const [updateData, setUpdateData] = useStateRef<FieldInstance[]>([]);
	const [formGroup, setFormGroup] = useState<FormGroupDef | null>(null);
	const [formGroupUpdate, setFormGroupUpdate] = useState<FormGroupDef | null>(null);
	const [updateObj, setUpdateObj] = useState<Record<string, FieldInstance & { nav?: string }>>({});
	const [showDeleteModal, setShowDeleteModal] = useState<ConfirmDeleteInterface | null>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [changed, setChanged] = useState(false);
	const [linkedData, setLinkedData] = useState<Record<string, FieldInstance>>({});
	const subUpdate = useRef(null);
	const subNew = useRef(null);

	const { dropdownRefs, openDropdownId, handleClick } = useClickOutside(dropdownData, userDoc);
	// NEED TO HAVE SUBS SETUP BEFORE THE FORM GROUP DATA RETRIEVAL
	// START WITH JUST THE ID, THEN SETUP THE SUBS, THEN GRAB AND SET TO formGroup
	useEffect(() => {
		let updateDataArray: FieldInstance[] = [];
		let formGroupUpdateDataArray: FormGroupDef[] = [];

		/**
		 * Sub for fieldInstance set by the form group.
		 * @param id of the form group to listen to.
		 */
		function setupUpdateSubscription(id: string) {
			if (online) {
				const client = generateClient({ authMode: "userPool" });
				subUpdate.current = client
					.graphql({
						query: newOnUpdatedFieldInstance,
						variables: { formGroup: id },
						authMode: "userPool",
					})
					// We need to kinda of group out the data here and because of the timing of situations
					// We have to collect all updates over 1second then setup the updateObj all at once.  This helps with a ton of issues that came up.
					// @ts-ignore
					.subscribe({
						next: (fieldData: { data: { newOnUpdatedFieldInstance: FieldInstance } }) => {
							updateDataArray.push(fieldData.data.newOnUpdatedFieldInstance);
						},
						error: (error: any) => {
							console.log("ERROR IN UPDATE FIELD INSTANCE: ", error);
						},
					});
			} else {
				// @ts-ignore
				subUpdate.current = liveQuery(async () => {
					const fields = (await db.fieldInstance.where("formGroup").equals(id).toArray()) as FieldInstance[];
					// Image fields are fucking stupid. when deleting an image, the flow will end up getting here, the field with the image is re-fetched, and that doesn't get the image
					// doc with it, so I have to do a special thing here to get it
					for (const field of fields) {
						if (field.imageId) {
							const image = await db.image.get(field.imageId);
							field.image = image;
						}
					}
					return fields;
				}).subscribe({
					next: (val) => {
						updateDataArray = updateDataArray.concat(val); // offline data is still leveraging the same system here.
					},
					error: (err) => {
						console.error("ERROR IN OFFLINE SUB", err);
					},
				});
			}
		}

		/**
		 * sub on the form group itself.
		 * @param id of the form group
		 */
		function setupUpdateFormGroupSubscription(id: string) {
			if (online) {
				const client = generateClient({ authMode: "userPool" });
				subNew.current = client
					.graphql({
						query: newOnUpdatedFormGroup,
						variables: { id },
						authMode: "userPool",
					})
					// @ts-ignore
					.subscribe({
						next: (listenerData: { data: { newOnUpdatedFormGroup: FormGroupDef } }) => {
							formGroupUpdateDataArray.push(listenerData.data.newOnUpdatedFormGroup); // This is a form group listener similar to field instances, but bugged right now.
						},
						error: (error: any) => {
							console.log("ERROR IN UPDATE FORM GROUP: ", error);
						},
					});
			}
		}

		async function retrieveFormGroup(id: string) {
			if (userDoc) {
				const retrievedFormGroup = await getSingleFormGroup(id);
				if (retrievedFormGroup) setupFormGroup(retrievedFormGroup);
			}
		}

		setupUpdateFormGroupSubscription(formGroupId);
		setupUpdateSubscription(formGroupId);
		retrieveFormGroup(formGroupId);

		const interval = setInterval(function () {
			if (updateDataArray.length > 0) {
				setUpdateData((prev) => [...prev, ...updateDataArray]);
				updateDataArray = [];
			}
			if (formGroupUpdateDataArray.length > 0) {
				setFormGroupUpdate((prev) => ({ ...prev, ...formGroupUpdateDataArray[0] }));
				formGroupUpdateDataArray = [];
			}
		}, 1000);

		return () => {
			// @ts-ignore
			subUpdate?.current?.unsubscribe();
			// @ts-ignore
			subNew?.current?.unsubscribe();
			clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [color.theme]);

	useEffect(() => {
		if (updateData && updateData.length > 0) {
			setupUpdateObj(updateData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateData, color.theme]);

	useEffect(() => {
		if (formGroupUpdate?.updatedAt && formGroup?.updatedAt && formGroupUpdate.updatedAt > formGroup.updatedAt) {
			if (formGroupUpdate.formOrder.length > formGroup.formOrder.length) {
				// it's a new form instance!
				const newId = formGroupUpdate.formOrder[formGroupUpdate.formOrder.length - 1];
				handleAddNewForm(newId);
			} else if (formGroupUpdate.formOrder.length < formGroup.formOrder.length) {
				// it's a deleted form instance!
				handleDeleteEvent(formGroupUpdate, formGroup);
			} else if (JSON.stringify(formGroupUpdate.formOrder) !== JSON.stringify(formGroup?.formOrder)) {
				// rearrange form instances
				handleRearrangeFormInstances(formGroupUpdate.formOrder);
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formGroupUpdate, formGroup, color.theme]);

	/**
	 * Retrieves a new form by id then adds it to state.  This is mainly for handling new forms in the real time
	 * collaboration piece from event listener
	 */
	async function handleAddNewForm(id: string) {
		try {
			let altIds = { ...linkedAltIds };
			let linked = { ...linkedData };
			const client = generateClient({ authMode: "userPool" });
			interface QueryResponse {
				getFormInstance: FormInstanceDef;
			}
			const newFormInstanceData = (await client.graphql({
				query: getFormInstance,
				variables: {
					id,
				},
				authMode: "userPool",
			})) as GraphQLResult<QueryResponse>;

			const newFormInstance = newFormInstanceData.data.getFormInstance;

			const theFormGroup = structuredClone(formGroup);
			if (theFormGroup?.formInstances) {
				theFormGroup.formInstances.items.push(newFormInstance);
				theFormGroup.formOrder.push(newFormInstance.id);
				setFormGroup(theFormGroup);

				const formHtmlObj = buildFormInstance(newFormInstance, altIds, linked, theFormGroup);
				if (formHtmlObj) {
					const formHtml = formHtmlObj.formHtml;
					altIds = formHtmlObj.altIds;
					linked = formHtmlObj.linked;
					setFormGroupHtml([...formGroupHtml, formHtml]);
					setLinkedAltIds(altIds);
					setLinkedData(linked);
				}
			}
		} catch (error) {
			recordSentry({ error, msg: "Error adding new form to state" });
		}
	}

	/**
	 * Updates data in the context hook state to be updated in field actions
	 * @param fieldData the new data.
	 */
	function setupUpdateObj(fieldDataArray: FieldInstance[]) {
		const newObj: Record<string, FieldInstance> = {};
		const newLinked: Record<string, FieldInstance> = {};
		for (let i = 0; i < fieldDataArray.length; i++) {
			const fieldData = fieldDataArray[i];
			const updated = fieldData;
			if (newObj[updated.id]) {
				const current = new Date(newObj[updated.id].updatedAt);
				const updatedTime = new Date(updated.updatedAt);
				// means theres an entry, so need to check last updated at and if the updated is newer, use that
				if (current < updatedTime) {
					newObj[updated.id] = updated;
				}
			} else {
				newObj[updated.id] = updated;
			}

			if (linkedData[updated.id]) {
				newLinked[updated.id] = updated;
			}
		}
		setUpdateObj((oldObj) => ({ ...oldObj, ...newObj }));
		setLinkedData((prev) => ({ ...prev, ...newLinked }));
		setUpdateData([]);
	}

	/**
	 * set the changed toggle for the "saved" status indicator
	 */
	function setupChanged(newChanged: boolean) {
		setChanged(newChanged);
	}

	/**
	 * When offline, this function exists so that when users update a field, we throw that field data into the process where the
	 * listener usually lives. This way, any updates to things (like linked fields) are caught and handled. Could probably be
	 * done better. This was part of commit 2b767dd "Linked fields update state, some of the merge logic" by me on 03/28/2023.
	 * Things have changed since then, this could too
	 * @param useFieldUpdatedField The data returned from the handleSubmit in useField
	 */
	function triggerUpdateDataForOffline(useFieldUpdatedField: { updatedField: FieldInstance }) {
		setUpdateData([useFieldUpdatedField.updatedField]);
	}

	/**
	 * Build out the html and context for each field
	 * also NEW this handles reordering the fields themselves.
	 */
	function setupFormGroup(retrieved?: FormGroupDef) {
		const theFormGroup = retrieved || formGroup;
		if (theFormGroup?.formInstances) {
			const formGroupHtmlFinal = [];
			const formInstancesFinal = [];
			const instanceMap: Record<string, { formHtml: React.JSX.Element; formInstance: FormInstanceDef }> = {};
			let altIds = { ...linkedAltIds };
			let linked = { ...linkedData };

			// Check if the object has items (on create we do not add the items yet, though that might be easier)
			const instances = theFormGroup.formInstances.items || theFormGroup.formInstances;
			for (let i = 0; i < instances.length; i++) {
				const formInstance = instances[i];
				const formHtmlObj = buildFormInstance(formInstance, altIds, linked, theFormGroup);
				if (formHtmlObj) {
					const formHtml = formHtmlObj.formHtml;
					altIds = formHtmlObj.altIds;
					linked = formHtmlObj.linked;
					instanceMap[formInstance.id] = {
						formHtml,
						formInstance,
					};
				}
			}
			for (let i = 0; i < theFormGroup.formOrder.length; i++) {
				const id = theFormGroup.formOrder[i];
				const instanceData = instanceMap[id];
				if (instanceData) {
					// For us to not get in here, the user would have a formGroup with an incorrect formsOrder (some id in the list that doesn't correspond to a fetched FG)
					formGroupHtmlFinal.push(instanceData.formHtml);
					formInstancesFinal.push(instanceData.formInstance);
				}
			}

			theFormGroup.formInstances.items = formInstancesFinal;

			// @ts-ignore
			setFormTemplates(theFormGroup.formTemplates);
			setFormGroup(theFormGroup);
			setFormGroupHtml(formGroupHtmlFinal);
			setLinkedAltIds(altIds);
			setLinkedData(linked);
		}
	}

	function createDefaultFieldAction(fieldInstance: FieldInstance, theFormGroup: FormGroupDef) {
		const html = (
			<div key={fieldInstance.id}>
				<UpdateContext.Consumer>
					{({ updateObj, setupChanged }) => (
						<FieldAction
							setupDrawer={setupDrawer}
							updateObj={updateObj[fieldInstance.id]}
							fieldInstance={fieldInstance}
							fieldInstanceId={fieldInstance.id}
							fieldTemplate={fieldInstance.fieldTemplate}
							setupChanged={setupChanged}
							formGroupType={theFormGroup.type}
							projectId={theFormGroup.projectId}
							online={online}
							userDoc={userDoc}
						/>
					)}
				</UpdateContext.Consumer>
			</div>
		);
		return html;
	}

	/**
	 * Setup the form instance html, and update linkedData and linkedAltIds.
	 * @param formInstance the form instance to be created
	 * @param altIds the alt instance ids that need to be stored and carried over
	 * (AKA) linkedAltIds
	 * @param linked data of the alt instance data to be carried over (AKA linkedData)
	 * @param theFormGroup that this form instance belongs to.
	 * @returns html, the finished linkedData and altIds.
	 */
	function buildFormInstance(
		formInstance: FormInstanceDef,
		altIds: Record<string, string>,
		linked: Record<string, FieldInstance>,
		theFormGroup: FormGroupDef,
		// index?: number
	) {
		if (formInstance.formTemplate?.fieldsOrder) {
			const final: React.JSX.Element[] = [];
			let fieldMap: Record<string, React.JSX.Element> = {};
			// Check if the object has items (on create we do not add the items yet, though that might be easier)
			// A form should have exactly one instance per field template. When it has more (a repair step once created
			// duplicate sets), render the populated one deterministically instead of whichever came last, and say so.
			const { selected: fieldInstancesToCheck, duplicates } = selectPrimaryFieldInstances(formInstance.fieldInstances.items as FieldInstance[]);
			if (Object.keys(duplicates).length > 0 && !reportedDuplicateForms.has(formInstance.id)) {
				reportedDuplicateForms.add(formInstance.id);
				// Describe every copy (winner and losers) with its age and row count, so a duplicate created after the
				// consolidation release is recognisable as a fresh divergence, and an already-consolidated loser (zero rows)
				// is recognisable as the known backlog.
				const allInstances = formInstance.fieldInstances.items as FieldInstance[];
				const inventory: Record<
					string,
					{ rendered: string; copies: { id: string; createdAt?: string; updatedAt?: string; rows: number }[]; newestCreatedAt: string }
				> = {};
				for (const templateId of Object.keys(duplicates)) {
					const copies = allInstances
						.filter((instance) => (instance.fieldTemplate?.id || instance.fieldTemplateId) === templateId)
						.map((instance) => ({
							id: instance.id,
							createdAt: instance.createdAt,
							updatedAt: instance.updatedAt,
							rows: instance.fieldsOrder?.length ?? 0,
						}));
					inventory[templateId] = {
						rendered: fieldInstancesToCheck.find((instance) => (instance.fieldTemplate?.id || instance.fieldTemplateId) === templateId)?.id ?? "",
						copies,
						newestCreatedAt: copies.reduce((newest, copy) => ((copy.createdAt ?? "") > newest ? copy.createdAt ?? "" : newest), ""),
					};
				}
				recordSentry({
					error: new Error(`Form ${formInstance.id} has more than one field instance for ${Object.keys(duplicates).length} template(s)`),
					msg: "Duplicate field instances on a form; rendered the populated one",
					additionalData: {
						formGroupId: theFormGroup.id,
						formInstanceId: formInstance.id,
						formTemplateId: formInstance.formTemplateId,
						duplicates,
						inventory,
					},
				});
			}

			if (fieldInstancesToCheck.length === 0 && formInstance.fieldInstances.nextToken) {
				// paginating at this point is probably going to be necessary
				throw new Error("NO FIELDS");
			}
			const fieldInstances: FieldInstance[] = [];
			// going to get the altInstances set up first now
			// This is to make sure that every field is accounted for and altInstances match up.
			for (let i = 0; i < fieldInstancesToCheck.length; i++) {
				const fieldInstanceToCheck = fieldInstancesToCheck[i] as FieldInstance;
				if (fieldInstanceToCheck?.fieldTemplate?.action && fieldInstanceToCheck.fieldTemplate.action.length > 0) {
					// it has a preform action so could be a linked field
					// action can have multiple, need to check if its a linked type so it can be setup at this point.
					let preformAction;
					let preformActionData;
					for (let i = 0; i < fieldInstanceToCheck.fieldTemplate.action.length; i++) {
						const action = fieldInstanceToCheck.fieldTemplate.action[i];
						if (action === "LINK") {
							preformAction = action;
							preformActionData = fieldInstanceToCheck.fieldTemplate.actionData[i];
							break;
						}
						if (action === "LINK_TO") {
							preformAction = action;
							preformActionData = `${formInstance.id}_${fieldInstanceToCheck.fieldTemplate.actionData[i]}`;
							break;
						}
					}

					if (preformAction === "LINK" || preformAction === "LINK_TO") {
						const actionId = preformActionData;
						if (actionId && !altIds[actionId]) {
							// the alt instance hasnt been setup in the map yet.
							if (fieldInstanceToCheck.altInstanceId) {
								altIds[actionId] = fieldInstanceToCheck.altInstanceId;
								if (!linked[fieldInstanceToCheck.altInstanceId] && fieldInstanceToCheck.altInstance) {
									linked[fieldInstanceToCheck.altInstanceId] = fieldInstanceToCheck.altInstance as FieldInstance;
								}
							} else {
								fieldInstanceToCheck.altInstanceId = uuid();
								const newFieldInstanceFirst: FieldInstance = {
									id: fieldInstanceToCheck.altInstanceId,
									value: fieldInstanceToCheck.altInstance?.value || "",
									authors: fieldInstanceToCheck.altInstance?.authors || [],
									updatedAt: fieldInstanceToCheck.altInstance?.updatedAt || new Date().toISOString(),
									altInstanceId: fieldInstanceToCheck.altInstanceId,
									formGroup: fieldInstanceToCheck.formGroup,
									group: fieldInstanceToCheck.group,
									tableFields: { items: [] },
									fieldsOrder: [],
									notes: "",
									editing: "",
									createdAt: fieldInstanceToCheck.altInstance?.createdAt || new Date().toISOString(),
									fieldTemplateId: null,
								};
								altIds[actionId] = fieldInstanceToCheck.altInstanceId;
								linked[fieldInstanceToCheck.altInstanceId] = newFieldInstanceFirst;
								fieldInstanceToCheck.altInstance = newFieldInstanceFirst;
							}
						}

						if (!fieldInstanceToCheck.altInstance && fieldInstanceToCheck.altInstanceId) {
							if (linked[fieldInstanceToCheck.altInstanceId]) {
								fieldInstanceToCheck.altInstance = linked[fieldInstanceToCheck.altInstanceId];
							}
						}

						if (!fieldInstanceToCheck.altInstance && !fieldInstanceToCheck.altInstanceId && actionId) {
							fieldInstanceToCheck.altInstanceId = altIds[actionId];
							fieldInstanceToCheck.altInstance = linked[fieldInstanceToCheck.altInstanceId];
						}
					}
				}
				// Register existing LINK alt instances from table/imageroll cells
				if (fieldInstanceToCheck.tableFields?.items?.length) {
					for (const cell of fieldInstanceToCheck.tableFields.items) {
						if (!cell.fieldTemplate?.action?.length) continue;
						for (let ci = 0; ci < cell.fieldTemplate.action.length; ci++) {
							const cellAction = cell.fieldTemplate.action[ci];
							let cellPreformAction: string | undefined;
							let cellActionId: string | undefined;
							if (cellAction === "LINK") {
								cellPreformAction = cellAction;
								cellActionId = cell.fieldTemplate.actionData[ci];
							}
							if (cellAction === "LINK_TO") {
								cellPreformAction = cellAction;
								cellActionId = `${formInstance.id}_${cell.fieldTemplate.actionData[ci]}`;
							}
							if ((cellPreformAction === "LINK" || cellPreformAction === "LINK_TO") && cellActionId) {
								if (!altIds[cellActionId] && cell.altInstanceId) {
									altIds[cellActionId] = cell.altInstanceId;
									if (!linked[cell.altInstanceId] && cell.altInstance) {
										linked[cell.altInstanceId] = cell.altInstance as FieldInstance;
									}
								}
								break;
							}
						}
					}
				}
				fieldInstances.push(fieldInstanceToCheck);
			}
			for (let i = 0; i < fieldInstances.length; i++) {
				const fieldInstance = JSON.parse(JSON.stringify(fieldInstances[i]));
				// If a fieldTemplate has been removed in the template manager, the fieldInstance will not have a fieldTemplate (but will have a fieldtemplateId)
				if (!fieldInstance.fieldTemplate) continue;
				// Setup the options for the field instance too if it has a nextToken
				if (fieldInstance.fieldTemplate.options?.nextToken) {
					fieldInstance.fieldTemplate.options.items = getOptions(fieldInstance.fieldTemplate);
				}
				if (fieldInstance.fieldTemplate.action && fieldInstance.fieldTemplate.action.length > 0) {
					let preformAction;
					let preformActionData;
					for (let i = 0; i < fieldInstance.fieldTemplate.action.length; i++) {
						const action = fieldInstance.fieldTemplate.action[i];
						if (action === "LINK") {
							preformAction = action;
							preformActionData = fieldInstance.fieldTemplate.actionData[i];
							break;
						}
						if (action === "LINK_FROM") {
							preformAction = action;
							preformActionData = fieldInstance.fieldTemplate.actionData[i];
							break;
						}
						if (action === "LINK_TO") {
							preformAction = action;
							preformActionData = `${formInstance.id}_${fieldInstance.fieldTemplate.actionData[i]}`;
							break;
						}
					}
					if (preformAction && preformActionData && (preformAction === "LINK" || preformAction === "LINK_TO")) {
						const newFieldInstance = {
							id: fieldInstance.altInstanceId,
							value: fieldInstance.altInstance?.value || "",
							revisions: fieldInstance.altInstance?.revisions || [],
							authors: fieldInstance.altInstance?.authors || [],
							updatedAt: fieldInstance.altInstance?.updatedAt || new Date().toISOString(),
							altInstanceId: fieldInstance.altInstanceId,
							formGroup: fieldInstance.formGroup,
							group: fieldInstance.group,
						};
						const html = (
							<UpdateContext.Consumer>
								{({ updateObj, setupChanged }) => (
									<FieldAction
										setupDrawer={setupDrawer}
										updateObj={updateObj[fieldInstance.altInstanceId]}
										fieldInstanceId={fieldInstance.id}
										fieldInstance={newFieldInstance}
										fieldTemplate={fieldInstance.fieldTemplate}
										setupChanged={setupChanged}
										online={online}
										userDoc={userDoc}
										formGroupType={theFormGroup.type}
										projectId={theFormGroup.projectId}
										triggerUpdateDataForOffline={triggerUpdateDataForOffline}
										formInstance={formInstance}
									/>
								)}
							</UpdateContext.Consumer>
						);

						fieldMap[fieldInstance.fieldTemplate.id] = html;
					} else if (preformAction === "LINK_FROM") {
						//  LINK FROMS ARE SIMILAR IN THAT THEY NEED TO MAKE SURE THE ALT INSTANCE EXISTS
						// BUT THEN THAT INSTANCE IS SETUP AS ALTUPDATEOBJ instead of UPDATEOBJ
						const actionDataParts = preformActionData.split("_");
						const linkFromFieldId = `${formInstance.id}_${actionDataParts[0]}`;
						const actionId = linkFromFieldId;
						let altFieldObjId = altIds[actionId];
						const html = (
							<UpdateContext.Consumer>
								{({ updateObj, setupChanged }) => (
									<FieldAction
										setupDrawer={setupDrawer}
										updateObj={updateObj[fieldInstance.id]}
										altUpdateObj={updateObj[altFieldObjId]}
										fieldInstanceId={fieldInstance.id}
										fieldInstance={fieldInstance}
										fieldTemplate={fieldInstance.fieldTemplate}
										setupChanged={setupChanged}
										online={online}
										userDoc={userDoc}
										formGroupType={theFormGroup.type}
										projectId={theFormGroup.projectId}
										triggerUpdateDataForOffline={triggerUpdateDataForOffline}
										formInstance={formInstance}
									/>
								)}
							</UpdateContext.Consumer>
						);

						fieldMap[fieldInstance.fieldTemplate.id] = html;
					} else {
						// can handle other action setup here
						// ********* VVVVVVV ***********
						const html = createDefaultFieldAction(fieldInstance, theFormGroup);
						fieldMap[fieldInstance.fieldTemplate.id] = html;
					}
				} else {
					// its not a linked field, and no actions
					const html = createDefaultFieldAction(fieldInstance, theFormGroup);
					fieldMap[fieldInstance.fieldTemplate.id] = html;
				}
			}

			// Organize the fields on the fullFieldArray
			for (let i = 0; i < formInstance.formTemplate.fieldsOrder.length; i++) {
				const orderedId = formInstance.formTemplate.fieldsOrder[i];
				const theFieldHtml = (
					<div key={orderedId} style={{}}>
						{fieldMap[orderedId]}
					</div>
				);

				final.push(theFieldHtml);
			}

			const formHtml = (
				// @ts-ignore
				<UpdateContext.Consumer key={formInstance.id} name={formInstance.id}>
					{({ updateObj }) => (
						// @ts-ignore
						<div key={formInstance.id} name={formInstance.id} style={{ borderBottom: `1px solid ${color.border.primary}` }}>
							<FormInstance
								updateObj={updateObj._nav}
								initialFormInstanceData={formInstance}
								online={online}
								formGroup={theFormGroup}
								colorPalette={color}
								id={formInstance.id}
								setProgress={setProgress}>
								{final}
							</FormInstance>
						</div>
					)}
				</UpdateContext.Consumer>
			);

			return { formHtml, altIds, linked };
		}
	}

	/**
	 * submits the creation of a new form instance and saves in db and state
	 * @param selection of the data for the new form instance and form group
	 */
	async function handleCreateNewFormSubmit(selection: FormTemplate) {
		let linked = { ...linkedData };
		const theFormGroup = JSON.parse(JSON.stringify(formGroup));
		// Need to retreive the full form template here!!!
		const fullSelection = await getFullFormTemplate(selection.id, online);
		if (fullSelection) {
			theFormGroup.updatedAt = new Date().toISOString();
			// Start checking if a new linked group of fields that is unique works here...
			const newForm = await createNewInstance(fullSelection, theFormGroup, linkedAltIds, linkedData);
			if (newForm) {
				// Just need to push into the items, the form group ordering should be updated already
				// from the event listener after updating the form group itself
				// If we do it here too we will have duplicates.
				theFormGroup.formInstances.items.push(newForm);
				// theFormGroup.formOrder.push(newForm.id);
				setFormGroup(theFormGroup);
				let altIds = { ...linkedAltIds };
				const htmlObj = buildFormInstance(newForm, altIds, linked, theFormGroup);
				if (htmlObj) {
					const html = htmlObj.formHtml;
					altIds = htmlObj.altIds;
					linked = htmlObj.linked;
					setFormGroupHtml([...formGroupHtml, html]);
					setLinkedAltIds(altIds);
					setLinkedData(linked);
				}
			}
		}
	}

	/**
	 * Rearranges the forms based on new form order and updates in state.
	 * @param newOrder of the form instances by id.
	 */
	function handleRearrangeFormInstances(newOrder: string[]) {
		if (formGroup) {
			const currentFormGroup = { ...formGroup };
			if (currentFormGroup.formInstances) {
				const instanceMap: Record<string, { formInstanceHtml: React.JSX.Element; formInstanceData: FormInstanceDef }> = {};
				const finalHtml = [];
				const finalData = [];
				for (let i = 0; i < formGroupHtml.length; i++) {
					const formInstanceHtml = formGroupHtml[i];
					const formInstanceData = currentFormGroup.formInstances.items[i];
					const key = formInstanceHtml.props.name;
					instanceMap[key] = { formInstanceHtml, formInstanceData };
				}

				for (let i = 0; i < newOrder.length; i++) {
					const id = newOrder[i];
					const instanceData = instanceMap[id];
					// https://factorearth.sentry.io/issues/6187134638/?alert_rule_id=15214387&alert_type=issue&environment=production&notification_uuid=5e45a135-7a31-4264-8ffb-35f93f164c26&project=4507131201912832&referrer=slack
					if (instanceData) {
						finalHtml.push(instanceData.formInstanceHtml);
						finalData.push(instanceData.formInstanceData);
					}
				}
				currentFormGroup.formInstances.items = finalData;
				currentFormGroup.formOrder = newOrder;
				currentFormGroup.updatedAt = new Date().toISOString();
				currentFormGroup.lastUpdatedBy = userDoc.username;
				setFormGroupHtml(finalHtml);
				setFormGroup(currentFormGroup);
			}
		}
	}

	/**
	 * Setup the instances in the new order.
	 * @param result ordered.
	 */
	function handleInitiateRearrangeFormInstances(result: DropResult) {
		const theFormGroup = { ...formGroup };
		if (!result.destination || !theFormGroup.formOrder) {
			// This means it was dragged out of bounds
			return;
		}
		// Apply the reorder synchronously inside onDragEnd. The drag library drops the
		// moved item into its final position and then expects React to render the new
		// order on the same tick. Deferring this (previously via setTimeout) left the
		// list in its original order for a frame before the state caught up, which
		// showed up as a flash/jump right after dropping.
		const newOrder = reorder(theFormGroup.formOrder, result.source.index, result.destination.index);
		handleRearrangeFormInstances(newOrder);
		updateFormGroupWithNewOrder(newOrder);
	}

	/**
	 * Updates cloud form group with the new order
	 * @param newOrder ids of the form instances in the new order
	 */
	function updateFormGroupWithNewOrder(newOrder: string[]) {
		if (formGroup) {
			const updatedFormGroup: FormGroupDef = { ...formGroup, formOrder: newOrder, updatedAt: new Date().toISOString(), lastUpdatedBy: userDoc.username };
			_updateFormGroup(updatedFormGroup, online);
		}
	}

	/**
	 * Deletes an instance from state and the db.
	 * @param instance to deletee
	 * @param index of the instance to remove
	 */
	async function deleteFormInstance(instance: FormInstanceDef, index: number) {
		if (formGroup) {
			const formGroupCopy = { ...formGroup };
			if (formGroupCopy.formInstances) {
				const formGroupHtmlCopy = [...formGroupHtml];
				formGroupCopy.formInstances.items.splice(index, 1);
				formGroupCopy.formOrder.splice(index, 1);
				formGroupCopy.updatedAt = new Date().toISOString();
				formGroupCopy.lastUpdatedBy = userDoc.username;
				formGroupHtmlCopy.splice(index, 1);
				if (online) {
					if (formGroupCopy.formInstances.items.length > 0) {
						deleteFormInstanceAmplify(instance, false, online);
					} else {
						deleteFormInstanceAmplify(instance, true, online);
					}
				} else {
					deleteFormI(instance, formGroupCopy);
				}
				setFormGroup(formGroupCopy);
				setFormGroupHtml(formGroupHtmlCopy);
				if (online) {
					// This is done in the deleteFormI function for offline people already
					updateFormGroupAmplify(formGroupCopy);
				}
			}
		}
	}

	function handleDeleteClick(instance: FormInstanceDef, index: number) {
		// Placeholder for any future functionality on delete click from options menu
		setShowDeleteModal({
			cb: () => deleteFormInstance(instance, index),
			docType: "Form Instance",
		});
		setDeleteModalOpen(true);
	}

	/**
	 * handles a delete event from the updateobj.
	 * @param newFormGroup new data for the form group.
	 * @param oldFormGroup old form group data
	 */
	function handleDeleteEvent(newFormGroup: FormGroupDef, oldFormGroup: FormGroupDef) {
		if (oldFormGroup.formInstances && formGroup) {
			let index;
			let oldId: string;
			for (let i = 0; i < oldFormGroup.formOrder.length; i++) {
				const id = oldFormGroup.formOrder[i];
				const indexOfId = newFormGroup.formOrder.indexOf(id);
				if (indexOfId === -1) {
					// need to remove form at i
					index = i;
					oldId = id;
				}
			}

			const newFormInstances = oldFormGroup.formInstances.items.filter((formInstance) => formInstance.id !== oldId);
			const updatedFormGroup: FormGroupDef = {
				...formGroup,
				formOrder: newFormGroup.formOrder,
				formInstances: {
					items: newFormInstances,
				},
			};

			const formGroupHtmlCopy = [...formGroupHtml];
			if (index) {
				formGroupHtmlCopy.splice(index, 1);
				setFormGroupHtml(formGroupHtmlCopy);
			}

			setFormGroup(updatedFormGroup);
		}
	}

	/**
	 * Sets the instance to the nav for scroll..
	 * @param id of the instance
	 */
	function handleSetInstanceToNav(id: string) {
		const newObj = {
			...updateObj,
			_nav: id,
		};
		// @ts-ignore
		setUpdateObj(newObj);
	}

	async function handleDuplicateFormInstance(formInstance: FormInstanceDef) {
		// Placeholder for any future functionality on duplicate click from options menu
		if (!formInstance.formGroup) {
			formInstance.formGroup = formGroup?.id!;
		}
		const duplicatedFormInstance = await duplicateFormInstance(formInstance, online);
		let linked = { ...linkedData };
		const theFormGroup = JSON.parse(JSON.stringify(formGroup));
		// Need to retrieve the full form template here!!!

		theFormGroup.updatedAt = new Date().toISOString();
		// Start checking if a new linked group of fields that is unique works here...
		if (duplicatedFormInstance) {
			// Just need to push into the items, the form group ordering should be updated already
			// from the event listener after updating the form group itself
			// If we do it here too we will have duplicates.
			theFormGroup.formInstances.items.push(duplicatedFormInstance);
			theFormGroup.formOrder.push(duplicatedFormInstance.id);
			await updateFormGroupWithNewOrder(theFormGroup.formOrder);
			setFormGroup(theFormGroup);
			let altIds = { ...linkedAltIds };
			const htmlObj = buildFormInstance(duplicatedFormInstance, altIds, linked, theFormGroup);
			if (htmlObj) {
				const html = htmlObj.formHtml;
				altIds = htmlObj.altIds;
				linked = htmlObj.linked;
				setFormGroupHtml([...formGroupHtml, html]);
				setLinkedAltIds(altIds);
				setLinkedData(linked);
			}
		}
	}

	// Field writes report their outcome to utils/connectionStatus. While any are failing, or the browser says it is
	// offline, the folder must not read "Saved": the values are being held in memory and resubmitted.
	const [connectionIssue, setConnectionIssue] = useState<string | null>(null);
	useEffect(() => {
		const update = () => {
			const failed = failedWriteCount();
			if (isConnectionDegraded()) {
				setConnectionIssue(
					failed > 0
						? `Connection problem: ${failed} field${failed === 1 ? "" : "s"} not saved yet. Your edits are kept on screen and will be retried.`
						: "Connection problem: edits may not be saving. Do not reload until the folder shows Saved.",
				);
			} else if (failed > 0) {
				setConnectionIssue(`${failed} field${failed === 1 ? "" : "s"} not saved yet, retrying.`);
			} else {
				setConnectionIssue(null);
			}
		};
		update();
		return subscribeToConnectionStatus(update);
	}, []);

	return (
		<UpdateContext.Provider value={{ updateObj, setupChanged, linkedAltIds, linkedData }}>
			<ConfirmDeleteFormInstanceModal open={deleteModalOpen} overlayObj={showDeleteModal} setOpen={setDeleteModalOpen} />
			{formGroup ? (
				<div data-id={formGroup.id} data-test="form-group-wrapper" className={styles.formGroupWrapper}>
					<div className={styles.savedBtn_wrapper}>
						<Badge
							label={changed || connectionIssue ? "Unsaved Changes" : "Saved"}
							theme={color}
							variant={changed || connectionIssue ? "outlined" : "success"}
						/>
						{connectionIssue && <Badge label={connectionIssue} theme={color} variant="outlined" data-test="connection-issue" />}
						{formGroup?.type === "default" && (
							// @ts-ignore
							<div ref={dropdownRefs.current[0]}>
								<Button
									colorPalette={color}
									id="formManager"
									data-test="form-manager"
									variant="outlined"
									label="Form Manager"
									className={`${styles.formOptions_ham} ${"ignoreClick"}`}
									onClick={() => handleClick(dropdownData[0].id)}
								/>
								{openDropdownId === dropdownData[0].id && (
									<FormGroupOptions
										deleteFormInstance={handleDeleteClick}
										formGroup={formGroup}
										formTemplates={formTemplates}
										handleCreateNewFormSubmit={handleCreateNewFormSubmit}
										handleInitiateRearrangeFormInstances={handleInitiateRearrangeFormInstances}
										project={project}
										setInstanceToNav={handleSetInstanceToNav}
										handleDuplicateFormInstance={handleDuplicateFormInstance}
										user={userDoc}
									/>
								)}
							</div>
						)}
					</div>
					<div
						className={styles.wrapper}
						style={{
							borderTop: `${color.border.primary} 1px solid`,
							borderLeft: `${color.border.primary} 1px solid`,
							borderRight: `${color.border.primary} 1px solid`,
							overflowY: "visible", // this is here to make the header for a form group sticky to the top of the tab d
						}}>
						{formGroupHtml.map((formHtml, index) => (
							<div
								key={index}
								style={{
									background: index % 2 === 0 ? color.background.primary : color.background.tertiary,
								}}>
								{formHtml}
							</div>
						))}
					</div>
				</div>
			) : formGroup === null ? (
				<LoadingSpinner />
			) : (
				<p style={{ color: color.utils.destructive }}>
					There was an error fetching this formGroup, please try again in a few minutes. If this issue persists, please contact the dev team
					(devs@factorearth.com) and tell them the name of the formGroup you tried to open, and the name of the project
				</p>
			)}
		</UpdateContext.Provider>
	);
}

interface ConfirmDeleteInterface {
	cb: () => void;
	docType: string;
}

interface ConfirmDeleteModalProps {
	open: boolean;
	overlayObj: ConfirmDeleteInterface | null;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function ConfirmDeleteFormInstanceModal(props: ConfirmDeleteModalProps) {
	const { open, overlayObj, setOpen } = props;

	const color = useTheme();

	function submit() {
		if (!overlayObj) return;
		overlayObj.cb();
		setOpen(false);
	}

	return (
		overlayObj && (
			<Modal open={open} colorPalette={color} setOpen={setOpen}>
				<DialogContainer style={{ zIndex: 10 }}>
					<ModalTitleContainer>
						<ModalTitle>Are you sure you want to delete this {overlayObj.docType}?</ModalTitle>
						<FiX onClick={() => setOpen(false)} size={24} />
					</ModalTitleContainer>
					<ModalFooter>
						<Button colorPalette={color} variant="destructive" label={"Delete"} onClick={submit} />
						<Button colorPalette={color} variant="outlined" label="Cancel" onClick={() => setOpen(false)} />
					</ModalFooter>
				</DialogContainer>
			</Modal>
		)
	);
}

export default FormGroup;
